import { CheckIcon, PlusIcon, TranslateIcon } from "@phosphor-icons/react";
import { type FormEvent, useCallback, useEffect, useState } from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { TextField } from "../components/Field";
import { LoadingState } from "../components/LoadingState";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import type { Locale } from "../types";

export function LocalesPage() {
  const [locales, setLocales] = useState<Locale[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState("");

  const load = useCallback(() => {
    setError("");
    apiRequest<Locale[]>("/api/v1/admin/locales")
      .then(setLocales)
      .catch(() => setError("Не удалось загрузить языки"));
  }, []);

  useEffect(load, [load]);

  async function update(locale: Locale, changes: Partial<Locale>) {
    setSavingId(locale.id);
    setError("");
    try {
      await apiRequest<Locale>(`/api/v1/admin/locales/${locale.id}`, {
        method: "PATCH",
        body: JSON.stringify(changes),
      });
      load();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось изменить язык",
      );
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="page">
      <PageHeader
        actions={
          <Button onClick={() => setShowCreate((value) => !value)}>
            <PlusIcon size={17} /> Добавить язык
          </Button>
        }
        description="Каждая локаль имеет собственные URL, черновики, SEO и публикации."
        eyebrow="Мультиязычность"
        title="Языки сайта"
      />

      {showCreate && (
        <CreateLocale onCancel={() => setShowCreate(false)} onCreated={load} />
      )}
      {error && <div className="notice notice--error">{error}</div>}

      {!locales ? (
        <LoadingState />
      ) : locales.length === 0 ? (
        <EmptyState
          description="Добавьте первый язык, чтобы создавать локализованные страницы."
          title="Языки ещё не настроены"
        />
      ) : (
        <section className="data-section">
          <div className="data-table data-table--locales">
            <div className="data-table__head">
              <span>Язык</span>
              <span>Код</span>
              <span>Hreflang</span>
              <span>Состояние</span>
              <span />
            </div>
            {locales.map((locale) => (
              <div className="data-table__row" key={locale.id}>
                <span className="locale-name">
                  <i>{locale.code.slice(0, 2).toUpperCase()}</i>
                  <span>
                    <strong>{locale.label}</strong>
                    {locale.is_default && <small>Основной язык</small>}
                  </span>
                </span>
                <span className="mono">{locale.code}</span>
                <span className="mono">{locale.hreflang}</span>
                <span>
                  <StatusBadge
                    status={locale.is_active ? "active" : "inactive"}
                  >
                    {locale.is_active ? "Активен" : "Отключён"}
                  </StatusBadge>
                </span>
                <span className="row-actions">
                  {!locale.is_default && (
                    <Button
                      disabled={savingId === locale.id}
                      onClick={() => void update(locale, { is_default: true })}
                      tone="quiet"
                    >
                      Сделать основным
                    </Button>
                  )}
                  <Button
                    disabled={locale.is_default || savingId === locale.id}
                    onClick={() =>
                      void update(locale, { is_active: !locale.is_active })
                    }
                    tone="secondary"
                  >
                    {locale.is_active ? "Отключить" : "Включить"}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function CreateLocale({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [hreflang, setHreflang] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiRequest<Locale>("/api/v1/admin/locales", {
        method: "POST",
        body: JSON.stringify({ code, label, hreflang, is_active: true }),
      });
      onCreated();
      onCancel();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось добавить язык",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="inline-create" onSubmit={submit}>
      <div className="inline-create__intro">
        <TranslateIcon size={22} />
        <div>
          <strong>Новая локаль</strong>
          <span>После создания язык сразу появится в редакторе страниц.</span>
        </div>
      </div>
      <div className="inline-create__fields">
        <TextField
          label="Код"
          onChange={(e) => setCode(e.target.value)}
          placeholder="de"
          required
          value={code}
        />
        <TextField
          label="Название"
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Deutsch"
          required
          value={label}
        />
        <TextField
          label="Hreflang"
          onChange={(e) => setHreflang(e.target.value)}
          placeholder="de-DE"
          required
          value={hreflang}
        />
      </div>
      {error && <span className="field__error">{error}</span>}
      <div className="inline-create__actions">
        <Button onClick={onCancel} tone="quiet" type="button">
          Отмена
        </Button>
        <Button disabled={saving} type="submit">
          <CheckIcon size={17} /> Создать
        </Button>
      </div>
    </form>
  );
}
